import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { TeamMemberWithUser } from '@/types/chat.types'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { AgentAvatar } from './AgentAvatar'
import { UserPlus, Search, X, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from '@/hooks/useTranslation'

interface AssignmentDropdownProps {
	conversationId: string
	currentAgent: TeamMemberWithUser | null | undefined
	teamMembers: TeamMemberWithUser[]
	programId: string
	variant?: 'default' | 'ghost' | 'outline'
	size?: 'default' | 'sm' | 'lg' | 'icon'
	className?: string
}

export function AssignmentDropdown({
	conversationId,
	currentAgent,
	teamMembers,
	programId,
	variant = 'outline',
	size = 'sm',
	className,
}: AssignmentDropdownProps) {
	const { t } = useTranslation()
	const [searchTerm, setSearchTerm] = useState('')
	const [open, setOpen] = useState(false)
	const queryClient = useQueryClient()
	const { toast } = useToast()

	const assignMutation = useMutation({
		mutationFn: async (agentId: string | null) => {
			if (agentId === null) {
				return apiClient(`/api/chat/conversations/${conversationId}/unassign`, {
					method: 'PATCH',
				})
			} else {
				return apiClient(`/api/chat/conversations/${conversationId}/assign`, {
					method: 'PATCH',
					data: { agentId },
				})
			}
		},
		onMutate: async (agentId) => {
			await queryClient.cancelQueries({ queryKey: ['conversations', programId] })
			await queryClient.cancelQueries({
				queryKey: ['conversation', conversationId],
			})

			const previousConversations = queryClient.getQueryData([
				'conversations',
				programId,
			])
			const previousConversation = queryClient.getQueryData([
				'conversation',
				conversationId,
			])

			const foundMember = agentId
				? teamMembers.find((m) => m.user_id === agentId)
				: null

			const assignedAgent = foundMember
				? {
						id: foundMember.user_id,
						name: foundMember.user.name,
						email: foundMember.user.email,
						avatar: foundMember.user.avatar_url,
						jobTitle: null,
						status: 'online'
				  }
				: null

			queryClient.setQueryData(['conversations', programId], (old: any) => {
				if (!old) return old
				return old.map((conv: any) =>
					conv.id === conversationId
						? { ...conv, assigned_agent_id: agentId, assigned_agent: assignedAgent }
						: conv
				)
			})

			queryClient.setQueryData(['conversation', conversationId], (old: any) => {
				if (!old) return old
				return { ...old, assigned_agent_id: agentId, assigned_agent: assignedAgent }
			})

			return { previousConversations, previousConversation }
		},
		onError: (error, agentId, context) => {
			if (context?.previousConversations) {
				queryClient.setQueryData(
					['conversations', programId],
					context.previousConversations
				)
			}
			if (context?.previousConversation) {
				queryClient.setQueryData(
					['conversation', conversationId],
					context.previousConversation
				)
			}

			toast({
				title: t('assignmentDropdown.assignmentFailed'),
				description: t('assignmentDropdown.failedToAssignConversation'),
				variant: 'destructive',
			})
		},
		onSuccess: (data, agentId) => {
			const assignedAgent = agentId
				? teamMembers.find((m) => m.user_id === agentId)
				: null

			const agentName = assignedAgent
				? (assignedAgent.user?.name || assignedAgent.user?.email)
				: null

			toast({
				title: t('assignmentDropdown.assignmentUpdated'),
				description: agentName
					? t('assignmentDropdown.assignedTo', { agentName })
					: t('assignmentDropdown.conversationUnassigned'),
			})

			setOpen(false)
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ['conversations', programId] })
			queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] })
		},
	})

	const filteredMembers = teamMembers.filter((member) => {
		const name = member.user.name?.toLowerCase() || ''
		const email = member.user.email.toLowerCase()
		const search = searchTerm.toLowerCase()
		return name.includes(search) || email.includes(search)
	})

	const handleAssign = (agentId: string | null) => {
		assignMutation.mutate(agentId)
	}

	const currentAgentId = currentAgent && ('user_id' in currentAgent ? currentAgent.user_id : (currentAgent as any).id)

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger asChild>
				<Button
					variant={variant}
					size={size}
					className={cn('gap-2 h-9', className)}
					disabled={assignMutation.isPending}>
					{currentAgent ? (
						<>
							<AgentAvatar agent={currentAgent} size="xs" showStatus={false} />
							<span className="hidden sm:inline text-sm">
								{t('assignmentDropdown.reassign')}
							</span>
						</>
					) : (
						<>
							<UserPlus className="h-4 w-4" />
							<span className="hidden sm:inline text-sm">{t('assignmentDropdown.assign')}</span>
						</>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-72 p-2">
				<DropdownMenuLabel className="text-sm font-semibold px-2">
					{t('assignmentDropdown.assignToAgent')}
				</DropdownMenuLabel>
				<DropdownMenuSeparator />

				<div className="px-2 py-2">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
						<Input
							placeholder={t('assignmentDropdown.searchAgents')}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-9 h-9 text-sm bg-muted/30 border-border/40 focus-visible:bg-background"
						/>
					</div>
				</div>

				<DropdownMenuSeparator />

				{currentAgent && (
					<>
						<DropdownMenuItem
							onClick={() => handleAssign(null)}
							className="text-orange-600 dark:text-orange-400 gap-2 px-3 py-2 cursor-pointer rounded-lg hover:bg-orange-500/10 transition-colors">
							<X className="h-4 w-4" />
							{t('assignmentDropdown.unassignConversation')}
						</DropdownMenuItem>
						<DropdownMenuSeparator />
					</>
				)}

				<div className="max-h-64 overflow-y-auto px-1">
					{filteredMembers.length === 0 ? (
						<div className="px-2 py-4 text-center text-sm text-muted-foreground">
							{t('assignmentDropdown.noAgentsFound')}
						</div>
					) : (
						filteredMembers.map((member) => {
							const isCurrentAgent = currentAgentId === member.user_id

							return (
								<DropdownMenuItem
									key={member.id}
									onClick={() => handleAssign(member.user_id)}
									className={cn(
										'px-3 py-2.5 gap-3 cursor-pointer rounded-lg',
										'hover:bg-primary/10 transition-colors',
										isCurrentAgent && 'bg-primary/5 ring-1 ring-primary/20'
									)}>
									<AgentAvatar agent={member} size="sm" showStatus={false} />
									<div className="flex-1 min-w-0">
										<div className="flex items-center gap-2">
											<span className="text-sm font-medium truncate">
												{member.user.name || member.user.email}
											</span>
											{isCurrentAgent && (
												<Check className="h-4 w-4 text-primary flex-shrink-0" />
											)}
										</div>
										<span className="text-xs text-muted-foreground truncate block">
											{member.user.email}
										</span>
									</div>
								</DropdownMenuItem>
							)
						})
					)}
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
